import { parseCanvasSectionKeyFromText } from '../../ai-agent/canvas/canvasSectionUtils';
import type { CanvasSectionRow } from './canonicalToCanvas';
import type { CanonicalResume } from '../schemas/resumeSchema';

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const PHONE_RE = /(?:\+?\d[\d\s().-]{7,}\d)/;
const LINKEDIN_RE = /(?:https?:\/\/)?(?:www\.)?linkedin\.com\/[^\s|]+/i;
const DATE_RANGE_RE =
  /((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{4})\s*[-–—]\s*((?:Present|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{4}))/i;

function emptyCanonical(): CanonicalResume {
  return {
    personalInfo: {
      fullName: '',
      email: '',
      phone: '',
      location: '',
    },
    professionalSummary: '',
    workExperience: [],
    education: [],
    skills: {
      technical: [],
      frameworksAndTools: [],
      managementAndDomain: [],
    },
  };
}

/** Check if a line looks like a work experience bullet (not a name). */
// Handle various Unicode bullet characters: • ● ○ ◦ ▪ ▸ ► ‣ ⁃ ∙ ⋅ ◾ ◽
const BULLET_CHARS_RE = /^[\u2022\u2023\u2043\u204C\u204D\u2219\u25AA\u25AB\u25B8\u25BA\u25CF\u25CB\u25E6\u2605\u2606\u2713\u2714\u2717\u2718\u27A2\u2981\u29BE\u29BF•●○◦▪▸►‣⁃∙⋅◾◽\-*]\s*/;

function isWorkExperienceLine(line: string): boolean {
  const t = line.trim();
  // Starts with a bullet (using comprehensive Unicode bullet pattern)
  if (BULLET_CHARS_RE.test(t)) {
    // Contains work experience action verbs
    return /\b(engineered|developed|led|managed|implemented|designed|built|created|delivered|achieved|drove|spearheaded|collaborated|established|improved|increased|reduced|launched|architected|integrated|optimized|automated|analyzed|executed|coordinated|directed|oversaw|mentored|trained|facilitated|streamlined|maintained|supported|resolved|deployed|migrated|defined|tracked)\b/i.test(t);
  }
  // Even without bullet, if it's clearly a work description
  if (t.length > 50 && /\b(engineered|developed|implemented|designed|built|created|delivered|achieved|spearheaded|collaborated|architected|integrated)\b/i.test(t)) {
    return true;
  }
  return false;
}

function parsePersonalInfo(
  content: string,
  fallback?: CanonicalResume['personalInfo'],
): CanonicalResume['personalInfo'] {
  const lines = content.split('\n').map((l) => l.trim()).filter(Boolean);
  const joined = content;
  const email = joined.match(EMAIL_RE)?.[0] ?? fallback?.email ?? '';
  const phone = joined.match(PHONE_RE)?.[0]?.trim() ?? fallback?.phone ?? '';
  const linkedin = joined.match(LINKEDIN_RE)?.[0] ?? fallback?.linkedin;
  
  // Find the first valid name line (not email, phone, url, or work experience bullet)
  const nameLine = lines.find((line) => {
    if (line.length <= 1 || line.length >= 80) return false;
    if (EMAIL_RE.test(line)) return false;
    if (PHONE_RE.test(line)) return false;
    if (LINKEDIN_RE.test(line)) return false;
    if (/github\.com/i.test(line)) return false;
    if (isWorkExperienceLine(line)) return false;
    // Skip lines that start with any bullet character
    if (BULLET_CHARS_RE.test(line)) return false;
    return true;
  });

  return {
    fullName: nameLine ?? fallback?.fullName ?? '',
    email,
    phone,
    location: fallback?.location ?? '',
    linkedin,
    github: fallback?.github,
  };
}

/**
 * Check if a title looks like a skills list rather than a job title.
 * Skills lists often contain comma-separated tech terms without job-related words.
 */
function looksLikeSkillsList(title: string): boolean {
  const TECH_TERMS = /\b(java|python|javascript|typescript|react|node|sql|aws|azure|gcp|docker|kubernetes|golang|go|spring|hibernate|angular|vue|c\+\+|c#|\.net|ruby|php|scala|kotlin|swift|rust|perl|bash|shell|linux|git|jenkins|terraform|ansible|kafka|redis|mongodb|postgresql|mysql|oracle|agile|scrum|safe|devops|api|rest|graphql|microservices|ml|ai)\b/gi;
  const JOB_WORDS = /\b(manager|engineer|developer|analyst|lead|director|architect|consultant|specialist|coordinator|associate|intern|senior|junior|head|chief|vp|vice president|president|officer|admin|executive|representative|assistant|owner|founder|ceo|cto|cfo|coo)\b/i;
  
  // Count tech terms
  const techMatches = (title.match(TECH_TERMS) || []).length;
  
  // If it has multiple tech terms separated by commas and no job words, it's likely skills
  if (techMatches >= 2 && title.includes(',') && !JOB_WORDS.test(title)) {
    return true;
  }
  
  return false;
}

function parseWorkExperience(content: string): CanonicalResume['workExperience'] {
  const blocks = content.split(/\n{2,}/).map((b) => b.trim()).filter(Boolean);
  const roles: CanonicalResume['workExperience'] = [];

  for (const block of blocks) {
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) continue;

    const header = lines[0].replace(/^\*\*|\*\*$/g, '');
    const dateMatch = block.match(DATE_RANGE_RE);
    const startDate = dateMatch?.[1]?.trim() ?? '';
    const endDate = dateMatch?.[2]?.trim() ?? '';

    let title = '';
    let company = '';
    const pipeParts = header.split('|').map((p) => p.trim()).filter(Boolean);
    if (pipeParts.length >= 2) {
      title = pipeParts[0].replace(/^\*\*|\*\*$/g, '').trim();
      company = pipeParts[1].replace(/^\*\*|\*\*$/g, '').trim();
    } else {
      title = header.replace(DATE_RANGE_RE, '').trim() || 'Role';
    }

    const highlights = lines
      .slice(1)
      .map((l) => l.replace(/^[•\-*]\s*/, '').trim())
      .filter((l) => l.length > 0 && !DATE_RANGE_RE.test(l));

    if (!title && !company && highlights.length === 0) continue;
    
    // Skip entries where the title looks like a skills list
    if (looksLikeSkillsList(title)) {
      console.warn('[parseWorkExperience] Skipping skills-like entry:', title);
      continue;
    }

    roles.push({
      title: title || 'Role',
      company: company || '',
      startDate,
      endDate,
      highlights,
    });
  }

  return roles;
}

function parseSkillsBlock(content: string): CanonicalResume['skills'] {
  const skills: CanonicalResume['skills'] = {
    technical: [],
    frameworksAndTools: [],
    managementAndDomain: [],
  };

  const lines = content.split('\n').map((l) => l.trim()).filter(Boolean);
  for (const line of lines) {
    const technical = line.match(/^technical:\s*(.+)$/i);
    const frameworks = line.match(/^(?:frameworks?\s*&\s*tools?|tools?):\s*(.+)$/i);
    const domain = line.match(/^(?:domain(?:\s+expertise)?|management(?:\s+and\s+domain)?):\s*(.+)$/i);
    const splitList = (raw: string) =>
      raw.split(/[,;|]/).map((s) => s.trim()).filter(Boolean);

    if (technical) skills.technical.push(...splitList(technical[1]));
    else if (frameworks) skills.frameworksAndTools.push(...splitList(frameworks[1]));
    else if (domain) skills.managementAndDomain.push(...splitList(domain[1]));
    else if (!line.includes(':')) {
      skills.technical.push(...splitList(line));
    }
  }

  return skills;
}

function parseEducation(content: string): CanonicalResume['education'] {
  return content
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/\s+[-–—]\s+/);
      if (parts.length >= 2) {
        return {
          degree: parts[0].trim(),
          institution: parts.slice(1, -1).join(' - ').trim() || parts[1].trim(),
          year: (parts[parts.length - 1].match(/\d{4}/) ?? [])[0] ?? '',
        };
      }
      return { degree: line, institution: '', year: '' };
    });
}

/** Convert optimized canvas rows → canonical JSON without regex re-bucketing. */
export function canvasSectionsToCanonicalResume(
  sections: CanvasSectionRow[],
  existing?: CanonicalResume | null,
): CanonicalResume {
  const out: CanonicalResume = existing
    ? structuredClone(existing)
    : emptyCanonical();

  for (const row of sections) {
    const key = parseCanvasSectionKeyFromText(row.title);
    const body = (row.content ?? '').trim();
    if (!body) continue;

    switch (key) {
      case 'personal':
        out.personalInfo = parsePersonalInfo(body, out.personalInfo);
        break;
      case 'summary':
        out.professionalSummary = body;
        break;
      case 'experience':
        out.workExperience = parseWorkExperience(body);
        break;
      case 'education':
        out.education = parseEducation(body);
        break;
      case 'skills':
        out.skills = parseSkillsBlock(body);
        break;
      case 'certifications':
        out.certifications = body.split('\n').map((l) => l.trim()).filter(Boolean);
        break;
      case 'awards':
        out.awards = body.split('\n').map((l) => l.trim()).filter(Boolean);
        break;
      default:
        break;
    }
  }

  return out;
}

/** Patch one optimized section into canonical JSON — no rebucketing. */
export function mergeOptimizedSectionIntoCanonical(
  canonical: CanonicalResume,
  sectionTitle: string,
  content: string,
): CanonicalResume {
  // NEVER merge Personal Information from optimizer output - it should always
  // come from the user's profile data, not from LLM-generated content which
  // may accidentally contain work experience bullets or other corrupted data.
  const key = parseCanvasSectionKeyFromText(sectionTitle);
  if (key === 'personal') {
    return canonical;
  }
  const row = { title: sectionTitle, content };
  return canvasSectionsToCanonicalResume([row], canonical);
}
